import { useMemo, useState } from 'react';
import ModuleSection from './ModuleSection';
import { POLLSTERS } from '../data/surveys';
import type { Poll } from '../data/surveys.types';
import { usePolls } from '../lib/usePolls';
import ChartLegend from './Surveys/charts/ChartLegend';
import ChartFullscreen from './Surveys/charts/ChartFullscreen';
import RawChart from './Surveys/charts/RawChart';
import { PROJECTED_VOTERS, leadToVotes } from '../lib/votes';
import { resolveSectionProps, useSectionText } from '../lib/useSectionText';
import './SurveyTracker.css';

const SECTION_DEFAULTS = {
  number: '01',
  kicker: 'The Polls',
  title: (
    <>
      Survey <em>Tracker</em>
    </>
  ),
  intro:
    'Every published survey from MaltaToday, Times of Malta and Vince Marmarà — tracked side-by-side, with leader trust ratings and district-level breakdowns where available. Updates the morning each new survey lands.',
};

// Most-recent non-anchor poll. POLLS is stored oldest-first, so we walk
// back from the end and skip the EP anchor entry.
function pickLatest(polls: Poll[]): Poll | null {
  for (let i = polls.length - 1; i >= 0; i--) {
    const p = polls[i];
    if (p.pollster !== '2024 EP Election (actual result)') return p;
  }
  return polls[polls.length - 1] ?? null;
}

const TEASER_LEGEND = [
  {
    label: 'MaltaToday',
    color: POLLSTERS.MaltaToday.color,
    style: 'line+dot' as const,
  },
  {
    label: 'Vince Marmarà',
    color: POLLSTERS.Sagalytics.color,
    style: 'line+dot' as const,
  },
  {
    label: 'Esprimi (ToM)',
    color: POLLSTERS['Esprimi (Times of Malta)'].color,
    style: 'line+dot' as const,
  },
  { label: 'EP 2024 result', color: '#b8860b', style: 'dot' as const },
];

export default function SurveyTracker() {
  const [fullscreen, setFullscreen] = useState(false);
  const { polls } = usePolls();
  const sectionText = useSectionText('surveys');

  const latest = useMemo(() => pickLatest(polls), [polls]);

  if (!latest) return null; // shouldn't happen — fallback always has data

  const pollster = POLLSTERS[latest.pollster];
  const latestDate = new Date(latest.date).toLocaleDateString('en-MT', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
  const winner = latest.lead > 0 ? 'PL' : 'PN';
  const sign = latest.lead > 0 ? '+' : '';
  const leadColor = latest.lead > 0 ? 'var(--party-pl)' : 'var(--party-pn)';
  const latestVotes = leadToVotes(latest.lead);

  return (
    <ModuleSection
      {...resolveSectionProps(SECTION_DEFAULTS, sectionText)}
      id="surveys"
    >
      <article className="survey-teaser">
        <div className="survey-teaser__chart-wrap">
          <button
            type="button"
            className="survey-teaser__expand"
            onClick={() => setFullscreen(true)}
            aria-label="Expand chart to interact"
            title="Expand chart to interact"
          >
            <span aria-hidden="true">⤢</span>
            <span className="survey-teaser__expand-label">Expand</span>
          </button>
          <ChartLegend items={TEASER_LEGEND} />
          <div className="survey-teaser__chart">
            <RawChart mode="compact" />
          </div>
        </div>

        <div className="survey-teaser__bottom">
          <div className="survey-teaser__latest">
            <span className="survey-teaser__latest-label">Latest reading</span>
            <span
              className="survey-teaser__latest-value"
              style={{ color: leadColor }}
            >
              {winner} {sign}
              {Math.abs(latest.lead).toFixed(1)}pp
            </span>
            <span className="survey-teaser__latest-votes">
              ≈ {latestVotes.toLocaleString('en-GB')} votes
              <span className="survey-teaser__latest-votes-meta">
                of ~{PROJECTED_VOTERS.toLocaleString('en-GB')} at 85% turnout
              </span>
            </span>
            <span className="survey-teaser__latest-meta">
              <span
                className="survey-teaser__pill"
                style={{ background: pollster.abbr_bg }}
              >
                {pollster.abbr}
              </span>
              {pollster.fullname} · {latestDate}
            </span>
          </div>
          <a href="/surveys" className="survey-teaser__cta">
            Full Data →
          </a>
        </div>
      </article>

      {fullscreen && (
        <ChartFullscreen
          title="Malta election polls — raw readings"
          subtitle="Drag to pan · scroll / pinch to zoom · double-click to reset · tap any dot for full poll details."
          legend={<ChartLegend items={TEASER_LEGEND} />}
          onClose={() => setFullscreen(false)}
        >
          <RawChart mode="fullscreen" />
        </ChartFullscreen>
      )}
    </ModuleSection>
  );
}
